import React, { useEffect, useRef, useState } from 'react'
import { PHOTO_CATEGORY_LABELS, PHOTO_CATEGORIES } from 'alakai-aloha-shared'
import { getPhotoPlannerRegionOptions } from '../../lib/photoPlannerConfig'

const DEFAULT_CATEGORIES = PHOTO_CATEGORIES

const toArray = (value) => (Array.isArray(value) ? value.filter(Boolean) : [])

const REGION_LABELS = {
  Other: 'Other (Offshore/Backcountry)'
}

export default function PhotoSpotsFilterBar({
  filters,
  onChange,
  categories = DEFAULT_CATEGORIES,
  island = 'kauai'
}) {
  const [open, setOpen] = useState(null)
  const filterRef = useRef(null)
  const regions = getPhotoPlannerRegionOptions(island)

  const toggleMulti = (field, value) => {
    const current = toArray(filters?.[field])
    const updated = current.includes(value) ? current.filter((v) => v !== value) : [...current, value]
    onChange({ ...filters, [field]: updated })
  }

  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'Escape') setOpen(null)
    }

    function handleClickOutside(e) {
      if (open && filterRef.current && !filterRef.current.contains(e.target)) {
        setOpen(null)
      }
    }

    document.addEventListener('keydown', handleKey)
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('keydown', handleKey)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  return (
    <>
      <div ref={filterRef} className="filter-bar">
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            placeholder="Search photo spots..."
            className="
              w-full px-4 py-2 bg-site-bg rounded border border-white text-white text-sm placeholder-[#E0E4E7]
              hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-[#63BCE5]
            "
            value={filters.searchText}
            onChange={(e) => onChange({ ...filters, searchText: e.target.value })}
          />

          <div className="relative">
            <button
              className="px-4 py-2 rounded bg-site-bg text-white text-sm border border-white hover:bg-gray-700 transition-colors flex items-center text-sm"
              onClick={() => setOpen(open === 'region' ? null : 'region')}
              type="button"
            >
              Region ▾
            </button>
            {open === 'region' ? (
              <div className="absolute mt-2 w-48 max-h-60 overflow-auto bg-site-bg border border-gray-600 rounded shadow-lg z-20">
                {regions.map((r) => {
                  const checked = toArray(filters.region).includes(r)
                  return (
                    <label key={r} className="flex items-center px-3 py-1 hover:bg-gray-700 cursor-pointer text-white">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleMulti('region', r)}
                        className="mr-2"
                      />
                      {REGION_LABELS[r] || r}
                    </label>
                  )
                })}
                <div className="p-2 border-t border-gray-700 text-center">
                  <button className="text-sm text-blue-400 hover:underline" onClick={() => setOpen(null)} type="button">
                    Close
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div className="relative">
            <button
              className="
                px-4 py-2 rounded bg-site-bg text-white text-sm border border-white hover:bg-gray-700 transition-colors
                flex items-center text-sm
              "
              onClick={() => setOpen(open === 'category' ? null : 'category')}
              type="button"
            >
              Category ▾
            </button>
            {open === 'category' ? (
              <div className="absolute mt-2 w-56 max-h-60 overflow-auto bg-site-bg border border-gray-600 rounded shadow-lg z-20">
                {categories.map((c) => {
                  const checked = toArray(filters.categories).includes(c)
                  return (
                    <label key={c} className="flex items-center px-3 py-1 hover:bg-gray-700 cursor-pointer text-white">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleMulti('categories', c)}
                        className="mr-2"
                      />
                      {PHOTO_CATEGORY_LABELS[c] || c}
                    </label>
                  )
                })}
                <div className="p-2 border-t border-gray-700 text-center">
                  <button className="text-sm text-blue-400 hover:underline" onClick={() => setOpen(null)} type="button">
                    Close
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {(toArray(filters.region).length > 0 || toArray(filters.categories).length > 0 || filters.searchText) ? (
          <div className="mt-2 flex flex-wrap gap-2 text-white">
            {toArray(filters.region).map((r) => (
              <span
                key={`sel-region-${r}`}
                className="flex items-center bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm"
              >
                {r}
                <button onClick={() => toggleMulti('region', r)} className="ml-1 font-bold hover:text-red-400" type="button">
                  ×
                </button>
              </span>
            ))}

            {toArray(filters.categories).map((c) => (
              <span
                key={`sel-category-${c}`}
                className="flex items-center bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm"
              >
                {PHOTO_CATEGORY_LABELS[c] || c}
                <button onClick={() => toggleMulti('categories', c)} className="ml-1 font-bold hover:text-red-400" type="button">
                  ×
                </button>
              </span>
            ))}

            {filters.searchText ? (
              <span className="flex items-center bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm">
                “{filters.searchText}”
                <button
                  onClick={() => onChange({ ...filters, searchText: '' })}
                  className="ml-1 font-bold hover:text-red-400"
                  type="button"
                >
                  ×
                </button>
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      <button
        className="text-sm hover:underline"
        type="button"
        onClick={() =>
          onChange({
            ...filters,
            region: [],
            categories: [],
            searchText: ''
          })
        }
      >
        Clear all filters
      </button>
    </>
  )
}
